import type { ObligationStatus } from '../compliance-obligations';
import { canonicalJson } from './pack-engine';

/**
 * Rule paragraph labels are retained as the source's own structure. They are
 * deliberately not collapsed into a claimed statutory field count.
 */
export const QCAB_PARAGRAPH_LABELS = [
  {
    label: 'r 158-15(a)(i)',
    heading: 'Feedback from people receiving care',
    required: true,
    sourceHint: 'Feedback record identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(a)(i)',
    unverified: false,
  },
  {
    label: 'r 158-15(a)(ii)',
    heading: 'Feedback from responsible persons and aged care workers',
    required: true,
    sourceHint: 'Feedback record identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(a)(ii)',
    unverified: false,
  },
  {
    label: 'r 158-15(b)',
    heading: 'Complaints and provider actions',
    required: true,
    sourceHint: 'Complaint and improvement identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(b)',
    unverified: false,
  },
  {
    label: 'r 158-15(c)',
    heading: 'Commissioner regulatory mechanisms',
    required: true,
    sourceHint: 'Regulatory action identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(c)',
    unverified: false,
  },
  {
    label: 'r 158-15(d)',
    heading: 'Continuous improvement progress',
    required: true,
    sourceHint: 'Improvement plan identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(d)',
    unverified: false,
  },
  {
    label: 'r 158-15(e)',
    heading: 'Commissioner audit results',
    required: true,
    sourceHint: 'Audit record identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(e)',
    unverified: false,
  },
  {
    label: 'r 158-15(f)(i)',
    heading: 'Allied health and practitioner availability',
    required: true,
    sourceHint: 'Workforce evidence identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(f)(i)',
    unverified: false,
  },
  {
    label: 'r 158-15(f)(ii)',
    heading: 'Registered nurse availability',
    required: true,
    sourceHint: 'Workforce evidence identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(f)(ii)',
    unverified: false,
  },
  {
    label: 'r 158-15(f)(iii)',
    heading: 'Aged care worker turnover',
    required: true,
    sourceHint: 'Workforce evidence identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(f)(iii)',
    unverified: false,
  },
  {
    label: 'r 158-15(g)',
    heading: 'Reportable incidents and provider actions',
    required: true,
    sourceHint: 'SIRS incident identifiers for the report period.',
    citation: 'Aged Care Rules 2025 r 158-15(g)',
    unverified: false,
  },
  {
    label: 'r 158-15(h)(i)',
    heading: 'Residential food quality feedback',
    required: false,
    sourceHint: 'Food feedback identifiers where residential care applies.',
    citation: 'Aged Care Rules 2025 r 158-15(h)(i)',
    unverified: false,
  },
  {
    label: 'r 158-15(h)(ii)',
    heading: 'Residential food quality and preparation changes',
    required: false,
    sourceHint: 'Food quality and preparation record identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(h)(ii)',
    unverified: false,
  },
  {
    label: 'r 158-15(h)(iii)',
    heading: 'Dietitian menu assessments',
    required: false,
    sourceHint: 'Accredited practising dietitian assessment identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(h)(iii)',
    unverified: false,
  },
  {
    label: 'r 158-15(h)(iv)',
    heading: 'Residential quality indicators',
    required: false,
    sourceHint: 'Quality indicator assessment identifiers.',
    citation: 'Aged Care Rules 2025 r 158-15(h)(iv)',
    unverified: false,
  },
] as const;

export type QcabParagraphLabel = (typeof QCAB_PARAGRAPH_LABELS)[number]['label'];

export interface QcabReportItem {
  paragraphLabel: QcabParagraphLabel;
  sourceRecordIds: readonly string[];
}

export interface BuildQcabReportPackInput {
  asOf: string;
  period: { start: string; end: string };
  bodyId: string;
  templateVersion: string;
  legalSourceVersion: string;
  resolverStatus: ObligationStatus;
  items: readonly QcabReportItem[];
  sha256Hex: (canonical: string) => string;
}

export interface QcabReportPack {
  kind: 'QCAB_REPORT';
  manifest: {
    asOf: string;
    period: { start: string; end: string };
    bodyId: string;
    templateVersion: string;
    legalSourceVersion: string;
    obligationId: 'quality_care_advisory_body';
    resolverStatus: ObligationStatus;
    legalCitation: 'Aged Care Rules 2025 r 158-15';
  };
  sections: readonly {
    paragraphLabel: QcabParagraphLabel;
    heading: string;
    required: boolean;
    sourceHint: string;
    citation: string;
    unverified: boolean;
    sourceRecordIds: readonly string[];
  }[];
  contentDigest: string;
}

function compare(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

/**
 * Builds a bounded evidence assembly. Names and narrative stay in linked
 * provider records; the immutable pack carries paragraph labels and IDs only.
 */
export function buildQcabReportPack(input: BuildQcabReportPackInput): QcabReportPack {
  if (!input.bodyId || !input.period.start || !input.period.end) {
    throw new Error('qcab_report_scope_required');
  }
  const allowed = new Set<string>(QCAB_PARAGRAPH_LABELS.map((entry) => entry.label));
  if (input.items.some((item) => !allowed.has(item.paragraphLabel))) {
    throw new Error('qcab_report_unknown_paragraph_label');
  }
  if (!input.items.some((item) => item.sourceRecordIds.length > 0)) {
    throw new Error('qcab_report_evidence_required');
  }
  const sourceRecordIdsByLabel = new Map<QcabParagraphLabel, Set<string>>();
  for (const item of input.items) {
    const ids = sourceRecordIdsByLabel.get(item.paragraphLabel) ?? new Set<string>();
    for (const id of item.sourceRecordIds) {
      ids.add(id);
    }
    sourceRecordIdsByLabel.set(item.paragraphLabel, ids);
  }
  const sections = QCAB_PARAGRAPH_LABELS.map((definition) => ({
    paragraphLabel: definition.label,
    heading: definition.heading,
    required: definition.required,
    sourceHint: definition.sourceHint,
    citation: definition.citation,
    unverified: definition.unverified,
    sourceRecordIds: [...(sourceRecordIdsByLabel.get(definition.label) ?? [])].sort(compare),
  }));
  const draft: Omit<QcabReportPack, 'contentDigest'> = {
    kind: 'QCAB_REPORT',
    manifest: {
      asOf: input.asOf,
      period: input.period,
      bodyId: input.bodyId,
      templateVersion: input.templateVersion,
      legalSourceVersion: input.legalSourceVersion,
      obligationId: 'quality_care_advisory_body',
      resolverStatus: input.resolverStatus,
      legalCitation: 'Aged Care Rules 2025 r 158-15',
    },
    sections,
  };
  const { asOf: _asOf, ...digestManifest } = draft.manifest;
  const contentDigest = input.sha256Hex(canonicalJson({ ...draft, manifest: digestManifest }));
  return { ...draft, contentDigest };
}

/**
 * Calculates the statutory six-month outer boundary. A tenant may bring the
 * target forward, never push it later; invalid values are refused explicitly.
 */
export function nextQcabReportDue(lastReportPeriodEnd: string, tightenToMonths?: number): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(lastReportPeriodEnd)) {
    throw new Error('qcab_report_period_end_invalid');
  }
  const months = tightenToMonths ?? 6;
  if (!Number.isInteger(months) || months < 1 || months > 6) {
    throw new Error('qcab_report_cadence_must_not_exceed_six_months');
  }
  const parts = lastReportPeriodEnd.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error('qcab_report_period_end_invalid');
  }
  const original = new Date(Date.UTC(year, month - 1, day));
  if (
    original.getUTCFullYear() !== year ||
    original.getUTCMonth() !== month - 1 ||
    original.getUTCDate() !== day
  ) {
    throw new Error('qcab_report_period_end_invalid');
  }
  const anchor = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0),
  ).getUTCDate();
  return `${anchor.getUTCFullYear()}-${String(anchor.getUTCMonth() + 1).padStart(2, '0')}-${String(Math.min(day, lastDay)).padStart(2, '0')}`;
}
